import Brightness4OutlinedIcon from '@mui/icons-material/Brightness4Outlined';
import Brightness7OutlinedIcon from '@mui/icons-material/Brightness7Outlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ViewSidebarOutlinedIcon from '@mui/icons-material/ViewSidebarOutlined';
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  BellIcon,
  PersonIcon,
  MoonIcon,
  SunIcon,
  GlobeIcon,
  ExitIcon,
  QuestionMarkCircledIcon,
  DotFilledIcon,
} from '@radix-ui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getSessionGeneration } from '@shared/api/sessionTokens';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { navigationSections } from '@app/routing/navigation';
import { helixTokens } from '@design-system/theme/tokens';
import helixMark from '@design-system/assets/helix-mark.svg';
import { useRouteMotion } from '@design-system/motion/useRouteMotion';
import { DropdownChevron } from '@design-system/components/DropdownChevron';
import { useAuthStore } from '@features/auth/store/authStore';
import {
  getUserAvailability,
  logoutSession,
  updateUserAvailability,
} from '@features/auth/api/auth';
import type { AgentAvailability } from '@features/auth/api/auth';
import { hasPermission } from '@shared/auth/permissions';
import type { RoleKey } from '@shared/auth/permissions';
import { useUiStore } from '@shared/store/uiStore';
import { helixApi, queryKeys } from '@shared/api/helixApi';
import { GlobalSearchDialog, SearchShortcutHint } from './GlobalSearchDialog';
import { ErsaStartFlowButton } from '@features/inbox/ErsaStartFlowButton';
import { useChannelVisibility } from '@shared/channels/channelVisibility';
import { ChannelVisibilityBoundary } from '@shared/channels/ChannelVisibilityBoundary';
import { NotificationBell } from '@features/notifications/NotificationBell';
import { visibleDashboard } from '@shared/channels/visibleDashboard';

const roleLabels: Record<RoleKey, string> = {
  agent: 'roles.agent',
  senior: 'roles.senior',
  supervisor: 'roles.supervisor',
  knowledgeManager: 'roles.knowledgeManager',
  administrator: 'roles.administrator',
  readOnlyQa: 'roles.readOnlyQa',
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return 'HX';
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function HelixMark() {
  return (
    <Box
      component="img"
      src={helixMark}
      alt=""
      aria-hidden="true"
      sx={{
        width: 32,
        height: 32,
        display: 'block',
        flexShrink: 0,
      }}
    />
  );
}

function NavigationContent({ collapsed = false }: { collapsed?: boolean }) {
  const visibility = useChannelVisibility();
  const { t } = useTranslation();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const granted = useAuthStore((state) => state.user?.permissions ?? []);
  const closeMobile = useUiStore((state) => state.setMobileNavigationOpen);
  const profileName = user?.name?.trim() || t('common.profile');
  const profileInitials = getInitials(profileName);
  const profileRole = user === null ? t('roles.seniorTier') : t(roleLabels[user.role]);
  const dashboardQuery = useQuery({
    queryKey: [...queryKeys.dashboard, visibility.revision],
    queryFn: async () => visibleDashboard(await helixApi.dashboard(), visibility.types)!,
    staleTime: 60_000,
    enabled: hasPermission(granted, 'dashboard.view') && Boolean(visibility.data),
  });
  const navigationCounts = useMemo<Record<string, string>>(() => {
    const summary = dashboardQuery.data;
    if (!summary) return {};
    if (summary.navigationCounts)
      return Object.fromEntries(
        Object.entries(summary.navigationCounts).map(([path, count]) => [path, String(count)]),
      );
    const counts: Record<string, string> = {};
    const kpi = (id: string) => summary.kpis.find((item) => item.id === id)?.value;
    const channel = (name: string) =>
      summary.channelVolume.find((item) => item.channel.toLowerCase().includes(name))?.value;
    const set = (path: string, value: string | number | undefined) => {
      if (value !== undefined && value !== '—') counts[path] = String(value);
    };
    set('/tickets', kpi('open'));
    set('/inbox', kpi('waiting'));
    set('/inbox/whatsapp', channel('whatsapp'));
    set('/inbox/live-chat', channel('live chat'));
    set('/calls', channel('voice'));
    set(
      '/escalations',
      summary.attention.filter((item) => item.route.startsWith('/escalations')).length || undefined,
    );
    return counts;
  }, [dashboardQuery.data]);

  return (
    <Stack sx={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Box
        sx={{
          minHeight: helixTokens.layout.header,
          flexShrink: 0,
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? 0 : 1,
          justifyContent: collapsed ? 'center' : 'flex-start',
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <HelixMark />
        <Box sx={{ display: collapsed ? 'none' : 'block', minWidth: 0 }}>
          <Typography
            sx={{
              color: 'text.primary',
              fontSize: 14,
              fontWeight: 700,
              lineHeight: '20px',
            }}
          >
            {t('app.name')}
          </Typography>
          <Typography
            sx={{
              display: 'block',
              color: 'text.secondary',
              fontSize: 12,
              fontWeight: 400,
              lineHeight: '16px',
            }}
          >
            {t('app.tagline')}
          </Typography>
        </Box>
      </Box>
      <Box
        component="nav"
        aria-label={t('common.menu')}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
          px: 1.5,
          py: 1,
          scrollbarColor: (theme) => `${theme.palette.divider} transparent`,
          scrollbarGutter: 'stable',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 7 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'divider',
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
            borderRadius: 999,
          },
          '&::-webkit-scrollbar-thumb:hover': { bgcolor: 'text.disabled' },
        }}
      >
        {navigationSections.map((section) => {
          const items = section.items.filter(
            (item) =>
              hasPermission(granted, item.permission) &&
              (item.sourcePermissions?.some(
                (source) =>
                  visibility.enabled(source.code) && hasPermission(granted, source.permission),
              ) ??
                true) &&
              (item.additionalPermissions?.every((permission) =>
                hasPermission(granted, permission),
              ) ??
                true) &&
              Boolean(visibility.data) &&
              visibility.pathVisible(item.path),
          );
          if (items.length === 0) return null;
          return (
            <Box key={section.key}>
              <Typography
                sx={{
                  color: 'text.disabled',
                  display: collapsed ? 'none' : 'block',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  lineHeight: 1.5,
                  px: 1.25,
                  pt: 1.75,
                  pb: 0.75,
                  textTransform: 'uppercase',
                }}
              >
                {t(section.key)}
              </Typography>
              <List disablePadding>
                {items.map((item) => {
                  const selected =
                    location.pathname === item.path ||
                    (!['/dashboard', '/inbox', '/settings'].includes(item.path) &&
                      !(item.path === '/calls' && location.pathname.startsWith('/calls/cupola')) &&
                      location.pathname.startsWith(`${item.path}/`));
                  const Icon = item.icon;
                  const count = navigationCounts[item.path];
                  return (
                    <ListItemButton
                      key={item.path}
                      component={NavLink}
                      className="helix-nav-item"
                      to={item.path}
                      end={
                        item.path === '/inbox' ||
                        item.path === '/settings' ||
                        item.path === '/calls'
                      }
                      selected={selected}
                      aria-label={t(item.key)}
                      onClick={() => closeMobile(false)}
                      sx={{
                        borderRadius: '6px',
                        color: (theme) =>
                          selected
                            ? helixTokens.color[theme.palette.mode].primary.text
                            : theme.palette.text.secondary,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        minHeight: 36,
                        gap: collapsed ? 0 : 1.25,
                        px: collapsed ? 0 : 1.25,
                        py: 1,
                        '&.Mui-selected': {
                          bgcolor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? helixTokens.color.dark.primary.soft
                              : helixTokens.color.light.primary.soft,
                        },
                        '&.Mui-selected:hover': {
                          color: (theme) => helixTokens.color[theme.palette.mode].primary.text,
                          bgcolor: (theme) =>
                            theme.palette.mode === 'dark'
                              ? helixTokens.color.dark.primary.soft
                              : helixTokens.color.light.primary.soft,
                        },
                        '&:hover': { bgcolor: 'background.default', color: 'text.primary' },
                      }}
                      title={collapsed ? t(item.key) : undefined}
                    >
                      <ListItemIcon
                        sx={{
                          color: 'inherit',
                          justifyContent: 'center',
                          minWidth: 0,
                        }}
                      >
                        <Icon aria-hidden style={{ width: 18, height: 18, fontSize: 18 }} />
                      </ListItemIcon>
                      <ListItemText
                        sx={{ display: collapsed ? 'none' : 'block', my: 0 }}
                        primary={
                          <Typography
                            sx={{
                              fontSize: 13,
                              lineHeight: '20px',
                              fontWeight: selected ? 600 : 500,
                              letterSpacing: '-0.005em',
                            }}
                          >
                            {t(item.key)}
                          </Typography>
                        }
                      />
                      {count && count !== '0' && !collapsed ? (
                        <Box
                          component="span"
                          sx={{
                            alignItems: 'center',
                            bgcolor: (theme) =>
                              item.path === '/inbox' || item.path === '/escalations'
                                ? theme.palette.mode === 'dark'
                                  ? helixTokens.color.dark.semantic.danger.soft
                                  : helixTokens.color.light.semantic.danger.soft
                                : helixTokens.color[theme.palette.mode].primary.soft,
                            borderRadius: 999,
                            color:
                              item.path === '/inbox' || item.path === '/escalations'
                                ? 'error.main'
                                : (theme) => helixTokens.color[theme.palette.mode].primary.text,
                            display: 'inline-flex',
                            fontSize: 12,
                            fontWeight: 500,
                            lineHeight: '16px',
                            height: 20,
                            justifyContent: 'center',
                            minWidth: 20,
                            px: 0.75,
                          }}
                        >
                          {count}
                        </Box>
                      ) : null}
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>
      <Divider />
      <Stack
        direction="row"
        spacing={collapsed ? 0 : 1.15}
        sx={{
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          flexShrink: 0,
          p: 1.5,
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? helixTokens.color.dark.primary.soft
                : helixTokens.color.light.primary.soft,
            color: 'primary.main',
            fontSize: 10.5,
            fontWeight: 700,
          }}
        >
          {profileInitials}
        </Avatar>
        <Box sx={{ display: collapsed ? 'none' : 'block', minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1.25 }}>
            {profileName}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 12, mt: 0.15 }}>
            {profileRole}
          </Typography>
        </Box>
      </Stack>
    </Stack>
  );
}

export function AppShell() {
  const visibility = useChannelVisibility();
  const { t } = useTranslation();
  const theme = useTheme();
  const desktop = useMediaQuery(`(min-width: ${helixTokens.layout.desktopMinWidth}px)`);
  const location = useLocation();
  const navigate = useNavigate();
  const contentMotionRef = useRouteMotion(location.pathname);
  const queryClient = useQueryClient();
  const mode = useUiStore((state) => state.mode);
  const locale = useUiStore((state) => state.locale);
  const mobileOpen = useUiStore((state) => state.mobileNavigationOpen);
  const toggleMode = useUiStore((state) => state.toggleMode);
  const setLocale = useUiStore((state) => state.setLocale);
  const setMobileOpen = useUiStore((state) => state.setMobileNavigationOpen);
  const logout = useAuthStore((state) => state.logout);
  const user = useAuthStore((state) => state.user);
  const granted = useAuthStore((state) => state.user?.permissions ?? []);
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);
  const [entityAnchor, setEntityAnchor] = useState<HTMLElement | null>(null);
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);
  const [statusAnchor, setStatusAnchor] = useState<HTMLElement | null>(null);
  const [pendingAvailability, setPendingAvailability] = useState<AgentAvailability | null>(null);
  const [availabilityError, setAvailabilityError] = useState(false);
  const [availabilityWarnings, setAvailabilityWarnings] = useState<string[]>([]);
  const [lastAvailability, setLastAvailability] = useState<AgentAvailability | null>(null);
  const [desktopNavigationCollapsed, setDesktopNavigationCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const profileName = user?.name?.trim() || t('common.profile');
  const profileInitials = getInitials(profileName);
  const currentNavigation = useMemo(() => {
    const entries = navigationSections.flatMap((section) =>
      section.items.map((item) => ({ item, section })),
    );
    return (
      entries.find(({ item }) => location.pathname === item.path) ??
      entries
        .filter(
          ({ item }) => item.path !== '/dashboard' && location.pathname.startsWith(`${item.path}/`),
        )
        .sort((left, right) => right.item.path.length - left.item.path.length)[0]
    );
  }, [location.pathname]);
  const currentTitle = currentNavigation?.item.key ?? 'nav.profile';
  const availabilityKey = ['auth', 'availability', user?.id] as const;
  const availabilityQuery = useQuery({
    queryKey: availabilityKey,
    queryFn: getUserAvailability,
    enabled: user !== null,
    staleTime: 30_000,
    retry: false,
  });
  const agentStatus =
    pendingAvailability ?? availabilityQuery.data?.availability ?? ('available' as const);
  const availabilityMutation = useMutation({
    mutationFn: updateUserAvailability,
    onMutate: (status) => {
      setLastAvailability(status);
      setPendingAvailability(status);
      setAvailabilityError(false);
      setAvailabilityWarnings([]);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(availabilityKey, response);
      setPendingAvailability(null);
      setAvailabilityWarnings(response.syncWarnings?.map((warning) => warning.channel) ?? []);
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
    },
    onError: () => {
      setPendingAvailability(null);
      setAvailabilityError(true);
      void queryClient.invalidateQueries({ queryKey: availabilityKey });
    },
  });

  useEffect(() => {
    const onExpired = () => {
      logout();
      void navigate('/session-expired');
    };
    window.addEventListener('helix:session-expired', onExpired);
    return () => window.removeEventListener('helix:session-expired', onExpired);
  }, [logout, navigate]);

  useEffect(() => {
    // Clear the temporary drawer when switching to the desktop sidebar.
    if (desktop) setMobileOpen(false);
  }, [desktop, setMobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const desktopDrawerWidth = desktopNavigationCollapsed
    ? helixTokens.layout.sidebarCollapsed
    : helixTokens.layout.sidebar;
  const drawer = <NavigationContent collapsed={desktop && desktopNavigationCollapsed} />;
  return (
    <Box
      dir="ltr"
      data-testid="app-shell"
      data-navigation={desktop ? (desktopNavigationCollapsed ? 'collapsed' : 'expanded') : 'mobile'}
      style={{ direction: 'ltr' }}
      sx={{
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: locale === 'ar' ? 'row-reverse' : 'row',
        height: '100dvh',
        overflow: 'hidden',
      }}
    >
      <Box
        component="aside"
        dir={locale === 'ar' ? 'rtl' : 'ltr'}
        style={{ direction: locale === 'ar' ? 'rtl' : 'ltr' }}
        sx={{
          height: '100%',
          width: desktop ? desktopDrawerWidth : 0,
          flexShrink: 0,
          transition: theme.transitions.create('width', {
            duration: helixTokens.motion.layout,
            easing: helixTokens.motion.standard,
          }),
        }}
      >
        <Drawer
          variant={desktop ? 'permanent' : 'temporary'}
          open={desktop || mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          anchor="left"
          sx={{
            height: '100%',
            minHeight: 0,
            overflow: 'hidden',
            '& .MuiDrawer-paper': {
              height: desktop ? '100%' : undefined,
              maxHeight: desktop ? '100%' : undefined,
              minHeight: 0,
              position: desktop ? 'relative' : undefined,
              width: desktop ? desktopDrawerWidth : helixTokens.layout.sidebar,
              boxSizing: 'border-box',
              bgcolor: 'background.paper',
              borderColor: 'divider',
              overflow: 'hidden',
              transition: theme.transitions.create('width', {
                duration: helixTokens.motion.layout,
                easing: helixTokens.motion.standard,
              }),
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        dir={locale === 'ar' ? 'rtl' : 'ltr'}
        style={{ direction: locale === 'ar' ? 'rtl' : 'ltr' }}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
        }}
      >
        <Box
          component="header"
          aria-label={t(currentTitle)}
          sx={{
            height: helixTokens.layout.header,
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            zIndex: theme.zIndex.appBar,
            bgcolor: (theme) => `${theme.palette.background.paper}f2`,
            backdropFilter: 'blur(8px)',
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            px: { xs: 1, sm: 2 },
            gap: { xs: 0.5, sm: 1.5 },
          }}
        >
          <IconButton
            aria-label={t('common.menu')}
            onClick={() => setMobileOpen(true)}
            sx={{ display: desktop ? 'none' : 'inline-flex', flexShrink: 0 }}
          >
            <MenuRoundedIcon />
          </IconButton>
          <Tooltip
            title={
              desktopNavigationCollapsed ? t('common.openNavigation') : t('common.closeNavigation')
            }
          >
            <IconButton
              aria-label={
                desktopNavigationCollapsed
                  ? t('common.openNavigation')
                  : t('common.closeNavigation')
              }
              onClick={() => setDesktopNavigationCollapsed((value) => !value)}
              sx={{ display: desktop ? 'inline-flex' : 'none', flexShrink: 0 }}
            >
              <ViewSidebarOutlinedIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
          <ButtonBase
            aria-label={t('shell.openSearch')}
            onClick={() => setSearchOpen(true)}
            sx={{
              alignItems: 'center',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '6px',
              color: 'text.secondary',
              display: 'flex',
              flex: 1,
              minWidth: 0,
              maxWidth: 440,
              gap: 1,
              height: 32,
              justifyContent: 'flex-start',
              px: 1,
              textAlign: 'start',
              '&:hover': { bgcolor: 'background.default' },
            }}
          >
            <SearchRoundedIcon sx={{ color: 'text.secondary', fontSize: 17, flexShrink: 0 }} />
            <Typography
              noWrap
              sx={{
                color: 'text.secondary',
                flex: 1,
                fontSize: 12.5,
                fontWeight: 500,
              }}
            >
              {t(
                visibility.enabled('tickets') && visibility.enabled('whatsapp')
                  ? 'shell.searchShort'
                  : 'channelAdmin.searchPlaceholder',
              )}
            </Typography>
            <Box sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
              <SearchShortcutHint label="⌘K" compact />
            </Box>
          </ButtonBase>
          <GlobalSearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              flexShrink: 0,
              gap: { xs: 0.5, sm: 1.5 },
              marginInlineStart: 'auto',
            }}
          >
            {visibility.enabled('whatsapp') && hasPermission(granted, 'whatsapp.actions') ? (
              <ErsaStartFlowButton />
            ) : null}
            <ButtonBase
              aria-label={t('shell.selectEntity')}
              aria-haspopup="menu"
              onClick={(event) => setEntityAnchor(event.currentTarget)}
              sx={{
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '6px',
                display: { xs: 'none', sm: 'flex' },
                gap: 0.8,
                height: 32,
                px: 1.05,
                '&:hover': { bgcolor: 'background.default' },
              }}
            >
              <Box
                component="span"
                sx={{
                  bgcolor: (theme) => helixTokens.color[theme.palette.mode].primary.soft,
                  borderRadius: 0.75,
                  color: 'primary.main',
                  display: 'grid',
                  fontSize: 10,
                  fontWeight: 800,
                  height: 20,
                  placeItems: 'center',
                  width: 20,
                }}
              >
                EA
              </Box>
              <Typography
                sx={{
                  color: 'text.primary',
                  fontSize: 13,
                  fontWeight: 500,
                  display: { xs: 'none', md: 'block' },
                }}
              >
                Emirates Auction
              </Typography>
              <DropdownChevron />
            </ButtonBase>
            <Menu
              anchorEl={entityAnchor}
              open={Boolean(entityAnchor)}
              onClose={() => setEntityAnchor(null)}
            >
              <MenuItem selected onClick={() => setEntityAnchor(null)}>
                Emirates Auction
              </MenuItem>
            </Menu>
            <Tooltip title={t('common.help')}>
              <IconButton
                aria-label={t('common.help')}
                onClick={(event) => setHelpAnchor(event.currentTarget)}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                <HelpOutlineRoundedIcon sx={{ fontSize: 19 }} />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={helpAnchor}
              open={Boolean(helpAnchor)}
              onClose={() => setHelpAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setHelpAnchor(null);
                  setSearchOpen(true);
                }}
              >
                {t('common.search')} · ⌘K
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setHelpAnchor(null);
                  setDesktopNavigationCollapsed((value) => !value);
                }}
              >
                {desktopNavigationCollapsed
                  ? t('common.openNavigation')
                  : t('common.closeNavigation')}
              </MenuItem>
            </Menu>
            <NotificationBell />
            <Tooltip title={t('common.theme')}>
              <IconButton
                aria-label={t('common.theme')}
                onClick={toggleMode}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                {mode === 'light' ? (
                  <Brightness4OutlinedIcon sx={{ fontSize: 19 }} />
                ) : (
                  <Brightness7OutlinedIcon sx={{ fontSize: 19 }} />
                )}
              </IconButton>
            </Tooltip>
            <ButtonBase
              aria-label={t('common.agentStatus')}
              aria-haspopup="menu"
              disabled={availabilityMutation.isPending}
              onClick={(event) => setStatusAnchor(event.currentTarget)}
              sx={{
                alignItems: 'center',
                bgcolor: (theme) => {
                  const semantic = helixTokens.color[theme.palette.mode].semantic;
                  return agentStatus === 'available'
                    ? semantic.success.soft
                    : agentStatus === 'busy'
                      ? semantic.danger.soft
                      : semantic.warning.soft;
                },
                borderRadius: '999px',
                color: (theme) => {
                  const semantic = helixTokens.color[theme.palette.mode].semantic;
                  return agentStatus === 'available'
                    ? semantic.success.main
                    : agentStatus === 'busy'
                      ? semantic.danger.main
                      : semantic.warning.main;
                },
                display: { xs: 'none', md: 'flex' },
                gap: 0.75,
                height: 28,
                px: 1.15,
                '&:hover': { filter: 'brightness(0.98)' },
                '&.Mui-disabled': { opacity: 0.7 },
              }}
            >
              <Box
                component="span"
                sx={{ bgcolor: 'currentColor', borderRadius: '50%', height: 8, width: 8 }}
              />
              <Typography sx={{ color: 'inherit', fontSize: 12.5, fontWeight: 700 }}>
                {t(`common.${agentStatus}`)}
              </Typography>
              <DropdownChevron />
            </ButtonBase>
            <Menu
              anchorEl={statusAnchor}
              open={Boolean(statusAnchor)}
              onClose={() => setStatusAnchor(null)}
            >
              {(['available', 'busy', 'away'] as const).map((status) => (
                <MenuItem
                  key={status}
                  selected={status === agentStatus}
                  disabled={availabilityMutation.isPending}
                  onClick={() => {
                    setPendingAvailability(status);
                    setStatusAnchor(null);
                    availabilityMutation.mutate(status);
                  }}
                >
                  {t(`common.${status}`)}
                </MenuItem>
              ))}
            </Menu>
            <Tooltip title={profileName}>
              <IconButton
                aria-label={t('common.profile')}
                onClick={(event) => setProfileAnchor(event.currentTarget)}
                sx={{ width: 32, height: 32 }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: 11,
                    fontWeight: 800,
                    bgcolor: (theme) => helixTokens.color[theme.palette.mode].primary.soft,
                    color: 'primary.main',
                  }}
                >
                  {profileInitials}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={profileAnchor}
              open={Boolean(profileAnchor)}
              onClose={() => setProfileAnchor(null)}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: 240,
                    '& .MuiMenuItem-root': { minHeight: 32, fontSize: 14, px: 1.5, gap: 0.5 },
                    '& .MuiListItemIcon-root': {
                      minWidth: 24,
                      color: 'text.secondary',
                      '& svg': { width: 15, height: 15 },
                    },
                  },
                },
              }}
            >
              <Typography sx={{ px: 1.5, py: 0.75, fontSize: 12, color: 'text.secondary' }}>
                {profileName}
              </Typography>
              <Divider />
              <MenuItem
                onClick={() => {
                  setProfileAnchor(null);
                  void navigate('/notification-preferences');
                }}
              >
                <ListItemIcon>
                  <BellIcon />
                </ListItemIcon>
                {t('notification.preferences')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setProfileAnchor(null);
                  void navigate('/profile');
                }}
              >
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                {t('nav.profile')}
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  setProfileAnchor(null);
                  toggleMode();
                }}
              >
                <ListItemIcon>{mode === 'dark' ? <SunIcon /> : <MoonIcon />}</ListItemIcon>
                {t('common.theme')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setStatusAnchor(profileAnchor);
                  setProfileAnchor(null);
                }}
                disabled={availabilityMutation.isPending}
              >
                <ListItemIcon>
                  <DotFilledIcon />
                </ListItemIcon>
                {t('common.agentStatus')} · {t(`common.${agentStatus}`)}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setHelpAnchor(profileAnchor);
                  setProfileAnchor(null);
                }}
              >
                <ListItemIcon>
                  <QuestionMarkCircledIcon />
                </ListItemIcon>
                {t('common.help')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setProfileAnchor(null);
                  setLocale(locale === 'en' ? 'ar' : 'en');
                }}
              >
                <ListItemIcon>
                  <GlobeIcon />
                </ListItemIcon>
                {locale === 'en' ? 'العربية' : 'English'}
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  setProfileAnchor(null);
                  const generation = getSessionGeneration();
                  void logoutSession()
                    .catch(() => undefined)
                    .finally(() => {
                      if (getSessionGeneration() !== generation) return;
                      logout();
                      void navigate('/login');
                    });
                }}
              >
                <ListItemIcon>
                  <ExitIcon />
                </ListItemIcon>
                {t('common.signOut')}
              </MenuItem>
            </Menu>
          </Stack>
        </Box>
        <Snackbar
          open={availabilityError || availabilityWarnings.length > 0}
          onClose={() => {
            setAvailabilityError(false);
            setAvailabilityWarnings([]);
          }}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            severity={availabilityError ? 'error' : 'warning'}
            variant="filled"
            onClose={() => {
              setAvailabilityError(false);
              setAvailabilityWarnings([]);
            }}
            action={
              <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
                <Button
                  color="inherit"
                  size="small"
                  disabled={availabilityMutation.isPending}
                  onClick={() => {
                    if (lastAvailability) availabilityMutation.mutate(lastAvailability);
                  }}
                >
                  {t('common.retry')}
                </Button>
                <IconButton
                  aria-label={t('common.close')}
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setAvailabilityError(false);
                    setAvailabilityWarnings([]);
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            }
          >
            {availabilityError
              ? t('common.availabilityUpdateFailed')
              : t('common.availabilitySyncFailed', {
                  channels: availabilityWarnings
                    .map((channel) => t(channel === 'livechat' ? 'nav.liveChat' : 'nav.whatsapp'))
                    .join(', '),
                })}
          </Alert>
        </Snackbar>
        <Box
          component="main"
          id="main-content"
          sx={{
            bgcolor:
              [
                '/dashboard',
                '/reports',
                '/whatsapp/dashboard',
                '/live-chat/dashboard',
                '/notifications',
                '/notification-preferences',
                '/assigned-to-me',
                '/customers-feedback/live-chat',
                '/customers-feedback/recovery-tracking',
                '/customers-feedback/ea-customer-survey',
              ].includes(location.pathname) || /^\/tickets\/[^/]+\/?$/.test(location.pathname)
                ? 'background.paper'
                : undefined,
            scrollbarGutter:
              [
                '/dashboard',
                '/reports',
                '/whatsapp/dashboard',
                '/live-chat/dashboard',
                '/notifications',
                '/notification-preferences',
                '/assigned-to-me',
                '/customers-feedback/live-chat',
                '/customers-feedback/recovery-tracking',
                '/customers-feedback/ea-customer-survey',
              ].includes(location.pathname) || /^\/tickets\/[^/]+\/?$/.test(location.pathname)
                ? 'stable'
                : undefined,
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: location.pathname.startsWith('/inbox') ? 'hidden' : 'auto',
          }}
        >
          <Box
            ref={contentMotionRef}
            sx={
              location.pathname.startsWith('/inbox')
                ? { height: '100%', minHeight: 0 }
                : {
                    pt: { xs: 2, sm: '22px' },
                    px: { xs: 2, sm: '26px' },
                    pb: '60px',
                    maxWidth: helixTokens.layout.contentMaxWidth,
                    mx: 'auto',
                    width: '100%',
                  }
            }
          >
            <ChannelVisibilityBoundary>
              <Outlet />
            </ChannelVisibilityBoundary>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
